/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, effect, inject, input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';

import { ENVIRONMENT, Environment } from '@environment';
import { MovieDetailSkeletonComponent, MovieLoadingComponent } from '@usheru-hff/ui/movie-detail-page';
import {
  Crew,
  CrewListing,
  getCrewListingByRole,
  Movie,
  MovieCrewRole,
  MovieEpisode,
  Star
} from '@usheru-hff/shared/domain';
import { LogService, ScopedLogger, SharedUtilsModule } from '@usheru-hff/shared/utils';

/**
 * Component to display movie data related to people involved.
 *
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - use env values in their own method and subscribe to env changes
 * //TODO barbeitoalex propostal - subscribe to current language to update the translate pipes
 */
@Component({
  selector: 'ush-movie-credits',
  templateUrl: './movie-credits.component.html',
  styleUrls: ['./movie-credits.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TooltipModule, RouterModule, SharedUtilsModule, MovieDetailSkeletonComponent]
})
export class MovieCreditsComponent implements MovieLoadingComponent {
  private readonly logger: ScopedLogger = inject(LogService).scoped('movie-detail');
  /**
   * @docNotNeccesary
   */
  readonly movie = input<Movie | null>(null);
  readonly isLoadingData = input(false);
  readonly creditsTitle = $localize`:@@movie.menu.credits:Credits`;
  readonly productionCompanyTitle = $localize`:@@crew.role.production-company:Production company`;

  /**
   * @docNotNeccesary
   */
  producingCompanies: string | undefined;

  /**
   * @docNotNeccesary
   */
  crewListing: CrewListing[] = [];

  /**
   * Alternative of {@link crewListing}.
   * Each entry is `[key, keyOg, value]`: `key` is unique after parse (`executive producer%1`),
   * `keyOg` is the role label shown in the view (`executive producer`).
   */
  customCrew: [key: string, keyOg: string, value: string][] | undefined;

  /**
   * Environment option.
   */
  canCollapse = false;

  /**
   * Hide the content and shows only the header.
   */
  collapsed = false;

  /**
   * If {@link movie} has episodes set this value.
   */
  multipleSeasons!: boolean;

  get hasRenderableCredits(): boolean {
    return this.crewListing.length > 0 || (this.customCrew?.length ?? 0) > 0 || !!this.producingCompanies;
  }

  constructor(@Inject(ENVIRONMENT) private env: Environment) {
    const cd = inject(ChangeDetectorRef);

    this.canCollapse = this.env?.customPageParams?.movieCreditsCollapse;
    this.collapsed = this.canCollapse && this.env?.customPageParams?.movieCreditsCollapseInitial;

    effect(() => {
      const movie = this.movie();

      this.crewListing = [];
      this.customCrew = undefined;
      this.producingCompanies = undefined;
      this.multipleSeasons = false;

      if (!movie) {
        cd.markForCheck();
        return;
      }

      this.setMovieCrew(movie);
      if (movie.producingCompanies && this.env.customPageParams?.movieCreditsProducingCompanies) {
        this.producingCompanies = movie.producingCompanies.map(p => p.name).join(', ');
      }

      this.debugLog('credits state', {
        movie: movie.friendlyUrl || movie.title,
        movieCrewCount: movie.movieCrew?.length ?? 0,
        configuredRoleCount: this.env.movieCrew?.movieCrewRoleListing?.length ?? 0,
        renderedRoleCount: this.crewListing.length,
        customCrewCount: this.customCrew?.length ?? 0,
        hasProducingCompanies: !!this.producingCompanies
      });

      if (movie.movieCrew?.length && !this.hasRenderableCredits && !movie.customMovieCrew) {
        this.logger.warn('credits filtered out', {
          movie: movie.friendlyUrl || movie.title,
          configuredRoles: this.env.movieCrew?.movieCrewRoleListing ?? [],
          availableRoleKeys: this.getAvailableRoleKeys(movie.movieCrew),
          availableRoleNames: this.getAvailableRoleNames(movie.movieCrew),
          crewCount: movie.movieCrew.length
        });
      }

      cd.markForCheck();
    });
  }

  /**
   * Inits the {@link crewListing} or {@link customCrew} if the {@link movie} has customMovieCrew.
   */
  private setMovieCrew(movie: Movie) {
    // this.addProductionCompaniesIfExists();
    if (movie.customMovieCrew) {
      this.setCustomCredits(movie);
    } else if (movie.movieCrew) {
      this.setRegularCredits(movie);
    }
  }

  /**
   * Creates the {@link crewMember} from {@link movie}. Filter the roles availables
   * and tries to get this config from {@link Environment} (movieCrewPipe, movieCrewRoleListing).
   */
  private setRegularCredits(movie: Movie) {
    this.crewListing = [];
    let crewMembers: Crew[];
    let roles: MovieCrewRole[] = [];
    let movieCrew = movie.movieCrew;

    // Merge Episodes Crew Memebers
    if (movie.episodes) {
      const { episodes } = movie;
      const seasonExample = episodes[0].seasonNumber;
      this.multipleSeasons = episodes.filter(e => e.seasonNumber != seasonExample).length > 0;

      episodes.forEach(({ episodeCrew, episodeNumber, seasonNumber }) => {
        episodeCrew.map(ep => {
          ep.star.episodes = [{ episodeNumber, seasonNumber }];
          return ep;
        });
        movieCrew = movieCrew.concat(episodeCrew);
      });

      // remove duplicate and merge episodes
      movieCrew = movieCrew.reduce((crewFiltered, crewMemeber) => {
        const { star, crewRole } = crewMemeber;
        const { id: idStar, episodes } = star;
        const { id: idRol } = crewRole;
        const foundCrewMember = crewFiltered.find(cm => cm.star.id === idStar && cm.crewRole.id === idRol);
        if (foundCrewMember) {
          const { episodes: foundCrewMembreEpisodes } = foundCrewMember.star;
          if (foundCrewMembreEpisodes && !episodes) {
            foundCrewMember.star.episodes = episodes;
          } else if (foundCrewMembreEpisodes && episodes) {
            foundCrewMember.star.episodes.concat(foundCrewMembreEpisodes);
          }
        } else {
          crewFiltered.push(crewMemeber);
        }
        return crewFiltered;
      }, [] as Crew[]);

      // sort episodes and generate tooltip text
      movieCrew = movieCrew.map(crewMember => {
        const { star } = crewMember;
        const { episodes } = star;
        if (episodes) {
          episodes.sort((a, b) => {
            const { seasonNumber: seasonNumberA, episodeNumber: episodeNumberA } = a;
            const { seasonNumber: seasonNumberB, episodeNumber: episodeNumberB } = b;
            return seasonNumberA != seasonNumberB ? seasonNumberA - seasonNumberB : episodeNumberA - episodeNumberB;
          });
          star.episodestooltip = this.generateEpisodesTooltip(episodes);
        }
        return crewMember;
      });
    }

    // apply crew pipe if there is any for the channel
    if (this.env.movieCrew?.movieCrewPipe) {
      crewMembers = this.env.movieCrew.movieCrewPipe(movieCrew);

      // filter roles available
      roles = this.getRolesFromCrewMembers(crewMembers);
    } else if (this.env.movieCrew?.movieCrewRoleListing) {
      // for each role add a crewListing
      crewMembers = movieCrew;
      roles = this.env.movieCrew.movieCrewRoleListing;
    } else {
      // there is no roles or filtering whatsoever so display all the roles comming from dashboard
      crewMembers = movieCrew;
      roles = this.getRolesFromCrewMembers(movieCrew);
    }

    roles.forEach(role => {
      const crewListing = getCrewListingByRole(role, crewMembers);
      if (crewListing) {
        this.crewListing.push(crewListing);
      }
    });
  }

  private getAvailableRoleKeys(crew: Crew[]): string[] {
    return [...new Set(crew.map(member => member?.crewRole?.trMsg).filter((role): role is string => !!role))];
  }

  private getAvailableRoleNames(crew: Crew[]): string[] {
    return [...new Set(crew.map(member => member?.crewRole?.name).filter((role): role is string => !!role))];
  }

  /**
   * Creates the {@link customCrew} from the {@link movie} customMovieCrew.
   */
  private setCustomCredits(movie: Movie) {
    try {
      const { json, originals } = this.suffixDuplicateRootKeys(movie.customMovieCrew);
      const tmp = JSON.parse(json);
      this.customCrew = [];
      for (const key in tmp) {
        this.customCrew.push([key, originals.get(key) ?? key, this.formatCustomCrewValue(tmp[key])]);
      }
    } catch (error) {
      console.error('bad custom crew format');
    }
  }

  /**
   * JSON.parse keeps only the last value when a role is repeated.
   * Rename later copies at the root, before parse, to `executive producer%1`, `%2`, ...
   */
  private suffixDuplicateRootKeys(raw: string): { json: string; originals: Map<string, string> } {
    const originals = new Map<string, string>();
    const rootKeyCounts = new Map<string, number>();
    let result = '';
    let index = 0;
    let objectDepth = 0;

    while (index < raw.length) {
      const char = raw[index];

      if (char !== '"') {
        if (char === '{') {
          objectDepth++;
        } else if (char === '}') {
          objectDepth--;
        }
        result += char;
        index++;
        continue;
      }

      const end = this.endOfJsonString(raw, index);
      if (end < 0) {
        result += raw.slice(index);
        break;
      }

      const literal = raw.slice(index, end + 1);
      let cursor = end + 1;
      while (cursor < raw.length && /\s/.test(raw[cursor])) {
        cursor++;
      }
      const isRootKey = objectDepth === 1 && raw[cursor] === ':';

      if (!isRootKey) {
        result += literal;
        index = end + 1;
        continue;
      }

      const key = JSON.parse(literal) as string;
      const seen = rootKeyCounts.get(key) ?? 0;
      rootKeyCounts.set(key, seen + 1);
      if (seen === 0) {
        result += literal;
      } else {
        const suffixed = `${key}%${seen}`;
        originals.set(suffixed, key);
        result += JSON.stringify(suffixed);
      }
      index = end + 1;
    }

    return { json: result, originals };
  }

  private endOfJsonString(raw: string, start: number): number {
    let escape = false;
    for (let index = start + 1; index < raw.length; index++) {
      const char = raw[index];
      if (escape) {
        escape = false;
        continue;
      }
      if (char === '\\') {
        escape = true;
        continue;
      }
      if (char === '"') {
        return index;
      }
    }
    return -1;
  }

  private formatCustomCrewValue(value: unknown): string {
    if (Array.isArray(value)) {
      return value
        .map(member => this.normalizeCustomCrewMember(member))
        .filter(Boolean)
        .join(', ');
    }
    return this.normalizeCustomCrewMember(value);
  }

  private normalizeCustomCrewMember(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (typeof value === 'object' && 'name' in value && typeof (value as { name?: unknown }).name === 'string') {
      return (value as { name: string }).name;
    }
    return '';
  }

  /**
   * Get each rol with a least one crew member
   */
  private getRolesFromCrewMembers(crew: Crew[]): MovieCrewRole[] {
    const uniqueRoles: MovieCrewRole[] = [];

    crew.forEach(crewMem => {
      const isDuplicated = uniqueRoles.includes(crewMem.crewRole.trMsg);
      if (!isDuplicated) uniqueRoles.push(crewMem.crewRole.trMsg);
    });

    return uniqueRoles;
  }

  /**
   * @docNotNeccesary
   */
  toggleCollapse(value?: boolean) {
    if (this.canCollapse) this.collapsed = value || !this.collapsed ? true : false;

    // if (!this.collapsed) {
    //TODO Tracking - maybe track once por load?
    // }
  }

  generateEpisodesTooltip(episodes: MovieEpisode[]): string | null {
    if (episodes) {
      if (this.multipleSeasons) {
        let text = '';
        episodes
          .reduce((map, episode) => {
            const { seasonNumber } = episode;
            const foundSeason = map.get(episode.seasonNumber);
            if (foundSeason) {
              foundSeason.push(episode);
            } else {
              map.set(seasonNumber, [episode]);
            }
            return map;
          }, new Map<number, MovieEpisode[]>())
          .forEach((episodes, season) => {
            text += `Season ${season}: ${episodes.map(e => e.episodeNumber).join(', ')} | `;
          });
        text = text.slice(0, -3);
        return text;
      } else {
        return `Episodes: ${episodes.map(e => e.episodeNumber).join(', ')}`;
      }
    } else {
      return null;
    }
  }

  private debugLog(step: string, context?: unknown): void {
    this.logger.log(`credits:${step}`, context ?? '');
  }
}
